import { makeAutoObservable, runInAction, type IReactionDisposer } from 'mobx'
import { CanceledError } from 'axios'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { type IParamsGetCannedMessages, type IResponseCannedMessage } from 'entities/CannedMessages'
import { CannedMessagesApi } from 'entities/CannedMessages/api/savedReplies'

type Props = {
  filter: 'favorite' | 'unfavorite'
  setSavedReplay: (savedReplayResponse: IResponseCannedMessage) => void
}

export class ListSavedRepliesStore {
  private _disposeReactionLoadData: IReactionDisposer | null = null
  private _filter: 'favorite' | 'unfavorite' = 'unfavorite'
  private _setSavedReplay: ((savedReplayResponse: IResponseCannedMessage) => void) | null
  private _page = 1
  private _last_page = 0
  private _length = 60
  private _total: number | null = null
  private _loading = false
  private _parentParams: IParamsGetCannedMessages = {}
  private _cancelRequest = new RequestCancelAxios()

  constructor({ filter, setSavedReplay }: Props) {
    makeAutoObservable(this)

    this._filter = filter
    this._setSavedReplay = setSavedReplay
  }

  get loading() {
    return this._loading
  }

  get total() {
    return this._total
  }

  get paramsGetItems(): IParamsGetCannedMessages {
    return {
      ...this._parentParams,
      page: this._page,
      length: this._length,
      filter: this._filter,
    }
  }

  get hasMore() {
    return this._page <= this._last_page
  }

  get cancelRequest() {
    return this._cancelRequest
  }

  reset = () => {
    this._disposeReactionLoadData?.()
    this.clear()
  }

  clear = () => {
    this._loading = false
    this._last_page = 0
    this._page = 1
    this._total = null
  }

  loadData = async (params?: IParamsGetCannedMessages) => {
    if (params) this._parentParams = params

    if (this._page > 1 && !this.hasMore) {
      runInAction(() => {
        this._loading = false
      })
      return
    }
    try {
      this._cancelRequest.init()

      runInAction(() => {
        this._loading = true
      })

      const dataResponse = await CannedMessagesApi.getCannedMessages(this.paramsGetItems, {
        cancelToken: this._cancelRequest.token,
      })

      const data = dataResponse.data

      if (this._setSavedReplay && data.data) await Promise.all(data.data.map(this._setSavedReplay))

      runInAction(() => {
        this._last_page = data.last_page
        this._total = data.total
      })
    } catch (e) {
      if (e instanceof CanceledError) return
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  loadMore = () => {
    if (this._loading) return
    runInAction(() => {
      this._page = this._page + 1
      this._loading = true
      this.loadData()
    })
  }
}
